import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, LabelList, ResponsiveContainer } from 'recharts';
import {
  Card,
  Text,
  HStack,
  Box,
  Flex,
  Spinner,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useGetTotalOrder } from '@/services/dashboard';
import CustomSelect from '@/components/common/CustomSelect';

interface Category {
  category: string;
  total_value: number;
}

interface OrderBarDataItem {
  label: string;
  categories: Category[];
}

interface OrderBarData {
  data: OrderBarDataItem[];
  filter_type: string;
}

interface ChartDataItem {
  label: string;
  [key: string]: string | number;
}

interface LegendPayload {
  value: string;
  color: string;
}

interface CustomLegendProps {
  payload?: LegendPayload[];
}

const CustomLegend: React.FC<CustomLegendProps> = ({ payload }) => {
  return (
    <HStack spacing={4} justify="center">
      {payload?.map((entry: LegendPayload, index: number) => (
        <HStack align="center" key={`item-${index}`} spacing={2}>
          <Box w={4} h={4} bg={entry.color} borderRadius="sm" />
          <Text fontSize="sm">{entry.value}</Text>
        </HStack>
      ))}
    </HStack>
  );
};

const timeframeOptions = [
  { id: 'week', label: 'This Week' },
  { id: 'year', label: 'This Month' },
];

const OrderBarChart: React.FC = () => {
  const [timePeriod, setTimePeriod] = useState('week');
  const [orderValue, setOrderValue] = useState(false);
  const [quantity, setQuantity] = useState(true);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);

  const { data: orderBarData, isLoading } = useGetTotalOrder(timePeriod, orderValue, quantity) as {
    data: OrderBarData | undefined;
    isLoading: boolean;
  };
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (
      orderBarData &&
      orderBarData.data &&
      orderBarData.data.length > 0 &&
      orderBarData.data.some(
        (item: OrderBarDataItem) => item.categories && item.categories.length > 0,
      )
    ) {
      const formattedData = orderBarData.data.map((item: OrderBarDataItem, index: number) => {
        let label = isMobile ? item.label.substring(0, 3) : item.label;

        if (orderBarData?.filter_type === 'year' && isMobile) {
          if (index % 2 !== 0) {
            label = ''; // Hide alternating labels
          }
        }

        const categories = item.categories.reduce(
          (acc: ChartDataItem, category: Category) => {
            acc[category.category.toLowerCase()] = category.total_value;
            return acc;
          },
          { label } as ChartDataItem,
        );
        return categories;
      });
      setChartData(formattedData);
    } else {
      setChartData([]);
    }
  }, [orderBarData, timePeriod, isMobile]);

  const handleSelectChange = (value: string) => {
    if (value === 'week' || value === 'year') {
      setTimePeriod(value);
      setOrderValue(false);
      setQuantity(true);
    } else if (value === 'order') {
      setOrderValue(true);
      setQuantity(false);
    } else if (value === 'quantity') {
      setOrderValue(false);
      setQuantity(true);
    }
  };

  const periodText = timePeriod === 'week' ? 'week' : 'month';

  const legendPayload = [
    { value: 'Kurta', color: '#2B7F75' },
    { value: 'Sari', color: '#FFC46B' },
  ];

  return (
    <Card
      position="relative"
      borderColor="border.default60"
      p={4}
      shadow="none"
      borderWidth="1px"
      h="430px"
      w="100%"
      overflowX="hidden"
      overflowY="hidden"
    >
      <Flex
        flexDirection={{ base: 'column', md: 'row' }}
        align="center"
        justify={{ base: 'center', md: 'space-between' }}
        mb={{ base: 4, md: 6 }}
        gap={{ base: 2, md: 4 }}
        w="100%"
      >
        <Flex
          flexDirection="row"
          align="center"
          justify={{ base: 'center', md: 'flex-start' }}
          gap={2}
          w={{ base: '100%', md: 'auto' }}
        >
          <Text fontSize="xl" fontWeight="semibold" textAlign={{ base: 'center', md: 'left' }}>
            Total Order
          </Text>
          <Box width="fit-content" display={{ base: 'block', md: 'none' }}>
            <CustomSelect
              key={timePeriod}
              options={timeframeOptions}
              selectedValue={timePeriod}
              onChange={handleSelectChange}
              placeholder="Select timeframe"
            />
          </Box>
        </Flex>
        <CustomLegend payload={legendPayload} />
        <Box width="fit-content" display={{ base: 'none', md: 'block' }}>
          <CustomSelect
            key={timePeriod}
            options={timeframeOptions}
            selectedValue={timePeriod}
            onChange={handleSelectChange}
            placeholder="Select timeframe"
          />
        </Box>
      </Flex>

      {isLoading ? (
        <Flex height="340px" width="100%" justifyContent="center" alignItems="center">
          <Spinner />
        </Flex>
      ) : !orderBarData ||
        !orderBarData.data ||
        orderBarData.data.length === 0 ||
        orderBarData.data.every(
          (item: OrderBarDataItem) => !item.categories || item.categories.length === 0,
        ) ? (
        <Flex height="340px" width="100%" justifyContent="center" alignItems="center">
          <VStack spacing={1}>
            <Text color="text.primary" fontSize="xl" fontWeight="semibold">
              No data available
            </Text>
            <Text color=" tumbleweed" fontSize="sm">
              There are no orders to display for this {periodText}.
            </Text>
          </VStack>
        </Flex>
      ) : (
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              interval={0}
              axisLine={false}
              tickLine={false}
            />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip />
            <Bar dataKey="kurta" fill="#2B7F75" name="Kurta">
              <LabelList dataKey="kurta" position="top" />
            </Bar>
            <Bar dataKey="sari" fill="#FFC46B" name="Sari">
              <LabelList dataKey="sari" position="top" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default OrderBarChart;
